import { useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { ReportBuilderProvider, type ReportPhotoKey } from './report-builder-state';
import './reports.css';

interface BuilderState {
  /** Held BESIDE the selections so a Project change can be detected during render. */
  readonly projectId: string;
  readonly captureIds: ReadonlySet<string>;
  readonly photos: ReadonlyMap<string, ReadonlySet<ReportPhotoKey>>;
  readonly baselines: ReadonlyMap<string, ReadonlySet<ReportPhotoKey>>;
  readonly autoSelected: ReadonlyMap<string, ReadonlySet<ReportPhotoKey>>;
  readonly description: string;
  readonly includeBranding: boolean;
}

function emptyState(projectId: string): BuilderState {
  return {
    projectId,
    captureIds: new Set(),
    photos: new Map(),
    baselines: new Map(),
    autoSelected: new Map(),
    description: '',
    // **DEFAULT ON.** Brandon configured his company so his reports would carry
    // it; making him opt in on every report would be the friction Settings
    // exists to remove. The preview turns it off automatically when no branding
    // has been configured, so an unbranded account is never asked to choose.
    includeBranding: true,
  };
}

const EMPTY: ReadonlySet<ReportPhotoKey> = new Set();

function withKey<T>(
  map: ReadonlyMap<string, ReadonlySet<T>>,
  key: string,
  next: ReadonlySet<T>,
): ReadonlyMap<string, ReadonlySet<T>> {
  const copy = new Map(map);
  copy.set(key, next);
  return copy;
}

/**
 * THE REPORT BUILDER'S PARENT ROUTE — M10 / S10-02.
 *
 * It renders no screen of its own. It holds the in-progress report and lets the
 * two steps underneath it change: **choose Captures**, then **choose photos and
 * write the Description**.
 *
 * **THIS IS HALF OF WHAT MAKES ANDROID'S BACK BUTTON CORRECT.** Before this,
 * the second step was a variable inside one screen — invisible to history — so
 * Back left the builder entirely. Now each step is a real address, Back walks
 * between them, and **the layout is not unmounted while that happens**, so
 * nothing Brandon chose is lost.
 *
 * **THE OTHER HALF ARRIVED IN S10-03B, AND THIS COMMENT USED TO GET IT WRONG.**
 * It said Crewlink installed no Capacitor App plugin "so the native shell hands
 * Back straight to the web view". It does not: with nothing registered, Android
 * finished the Activity and the web history was never consulted. The history
 * shape here was right all along and simply unreachable. `SystemBackBridge`
 * (`src/app/`) now delivers the gesture into it.
 *
 * **A PROJECT CHANGE CLEARS EVERYTHING, AND THE RESET HAPPENS DURING RENDER.**
 * The same route pattern serves every Project, and React Router keeps this
 * component mounted when only the id in the address changes — so without this,
 * a report started in one Project could carry its Captures into another. An
 * effect would render one frame with the previous Project's selections still
 * live, which is long enough for a step to read them. **This is the convention
 * `CapturesList` already established** for exactly the same reason, and it is
 * copied rather than reinvented.
 */
export function ReportBuilderLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const activeProjectId = projectId ?? '';
  const [state, setState] = useState<BuilderState>(() => emptyState(activeProjectId));

  if (state.projectId !== activeProjectId) {
    setState(emptyState(activeProjectId));
  }

  const builder = useMemo(
    () => ({
      projectId: state.projectId,
      captureIds: state.captureIds,

      toggleCapture: (captureId: string) => {
        setState((current) => {
          const next = new Set(current.captureIds);
          if (next.has(captureId)) next.delete(captureId);
          else next.add(captureId);
          return { ...current, captureIds: next };
        });
      },

      photoKeysFor: (captureId: string) => state.photos.get(captureId) ?? EMPTY,

      togglePhoto: (captureId: string, key: ReportPhotoKey) => {
        setState((current) => {
          const chosen = new Set(current.photos.get(captureId) ?? EMPTY);
          if (chosen.has(key)) chosen.delete(key);
          else chosen.add(key);
          return { ...current, photos: withKey(current.photos, captureId, chosen) };
        });
      },

      baselineFor: (captureId: string) => state.baselines.get(captureId) ?? null,

      beginAdd: (captureId: string, existingKeys: readonly ReportPhotoKey[]) => {
        setState((current) => ({
          ...current,
          baselines: withKey(current.baselines, captureId, new Set(existingKeys)),
        }));
      },

      autoSelectedFor: (captureId: string) => state.autoSelected.get(captureId) ?? EMPTY,

      autoSelectAttached: (captureId: string, keys: readonly ReportPhotoKey[]) => {
        if (keys.length === 0) return;
        setState((current) => {
          const chosen = new Set(current.photos.get(captureId) ?? EMPTY);
          const claimed = new Set(current.autoSelected.get(captureId) ?? EMPTY);
          /**
           * **CLAIMED ONCE, EVER — and `claimed` is what makes that true.**
           * The Capture section re-mounts every time Brandon steps back and
           * forward, so its effect would otherwise re-select a photo he had
           * deliberately deselected. It filters on this record before calling,
           * which is why there is no second guard here: a check that can never
           * run reads as protection and provides none.
           */
          for (const key of keys) {
            claimed.add(key);
            chosen.add(key);
          }
          return {
            ...current,
            photos: withKey(current.photos, captureId, chosen),
            autoSelected: withKey(current.autoSelected, captureId, claimed),
          };
        });
      },

      description: state.description,
      setDescription: (next: string) => {
        setState((current) => ({ ...current, description: next }));
      },

      includeBranding: state.includeBranding,
      toggleBranding: () => {
        setState((current) => ({ ...current, includeBranding: !current.includeBranding }));
      },
    }),
    [state],
  );

  return (
    <ReportBuilderProvider value={builder}>
      <Outlet />
    </ReportBuilderProvider>
  );
}
